import { debounce } from '@mui/material/utils';
import { useEffect, useRef, useState } from 'react';

type ScrollPercentageCallback = (percentage: number) => void;
type Thresholds = number[];

const DEFAULT_THRESHOLDS: Thresholds = [25, 50, 75, 100];
const OBSERVER_OPTIONS = { root: null, threshold: 0 };

/**
 * Tracks scroll progress at thresholds.
 * @param config - optional object to define optional callback func and thresholds
 * @param config.onProgress
 * @param config.thresholds
 * @returns percentage as number
 */
export function useScrollPercentage(
  config: {
    onProgress?: ScrollPercentageCallback;
    thresholds?: Thresholds;
  } = {},
): number {
  const { onProgress, thresholds = DEFAULT_THRESHOLDS } = config;
  const [progress, setProgress] = useState(0);
  const seenThreshold = useRef<Record<number, boolean>>({});

  useEffect(() => {
    const markers: HTMLDivElement[] = [];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const threshold = Number(entry.target.getAttribute('data-threshold'));
        if (entry.isIntersecting && threshold && !seenThreshold.current[threshold]) {
          seenThreshold.current[threshold] = true;
          setProgress(threshold);
          onProgress?.(threshold);
        }
      });
    }, OBSERVER_OPTIONS);

    const placeMarkers = () => {
      markers.forEach((marker) => marker.remove());
      const pageHeight = document.documentElement.scrollHeight - 1;

      thresholds.forEach((threshold) => {
        const marker = document.createElement('div');
        marker.setAttribute('data-threshold', String(threshold));
        Object.assign(marker.style, {
          position: 'absolute',
          top: `${(pageHeight * threshold) / 100}px`,
          left: '0',
          width: '100%',
          height: '1px',
          pointerEvents: 'none',
        });
        document.body.appendChild(marker);
        observer.observe(marker);
        markers.push(marker);
      });
    };

    placeMarkers();

    const debouncedPlaceMarkers = debounce(placeMarkers, 500);

    window.addEventListener('resize', debouncedPlaceMarkers);

    return () => {
      window.removeEventListener('resize', debouncedPlaceMarkers);
      // clears pending debounce calls so we don't get more markers than expected
      debouncedPlaceMarkers.clear();
      observer.disconnect();
      markers.forEach((marker) => marker.remove());
    };
  }, [onProgress, thresholds]);

  return progress;
}
